import React, { useState } from 'react';
import ImageModal from '../ImageModal';
import { customerUploadMediaUrl } from '../productMediaUtils';
import { parseBespokePhotos } from './customerPhotoUpload';

function photoFileName(prefix, fieldLabel) {
  const safe = (s) =>
    String(s || '')
      .trim()
      .replace(/[^\w.-]+/g, '-')
      .replace(/-+/g, '-')
      .slice(0, 48);
  return `${safe(prefix) || 'photo'}-${safe(fieldLabel) || 'image'}.jpg`;
}

function printPhoto({ title, fieldLabel, imageUrl }) {
  const html = `<!DOCTYPE html><html><head><meta charset="utf-8"/><title>${title}</title>
<style>
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: "Segoe UI", system-ui, sans-serif; color: #111; padding: 12mm; }
  h1 { font-size: 14pt; margin-bottom: 4mm; }
  p { font-size: 10pt; color: #444; margin-bottom: 8mm; }
  img { max-width: 100%; max-height: 240mm; object-fit: contain; display: block; margin: 0 auto; }
  @media print { @page { size: A4 portrait; margin: 10mm; } body { padding: 0; } }
</style></head><body>
<h1>${title}</h1>
<p>${fieldLabel} · Printed ${new Date().toLocaleString('en-IN')}</p>
<img src="${imageUrl}" alt="Customer photo" />
<script>window.onload=function(){setTimeout(function(){window.print();},400);};</script>
</body></html>`;
  const w = window.open('', '_blank');
  if (!w) {
    window.alert('Allow pop-ups to print photos.');
    return;
  }
  w.document.write(html);
  w.document.close();
}

/**
 * Photo preview with download / print / lightbox for admin workshop views.
 */
export default function AdminCustomerPhotoBlock({
  url,
  label = 'Customer photo',
  filePrefix = 'photo',
  className = '',
}) {
  const [showFullSize, setShowFullSize] = useState(false);
  const src = customerUploadMediaUrl(url);
  if (!src) return null;

  return (
    <>
      <div className={`admin-customer-photo ${className}`.trim()}>
        <button
          type="button"
          className="admin-customer-photo__link btn btn-link p-0 border-0"
          onClick={() => setShowFullSize(true)}
          aria-label={`View full size: ${label}`}
        >
          <img src={src} alt={label} className="admin-customer-photo__img" />
        </button>
        <div className="admin-customer-photo__actions">
          <a
            className="btn btn-sm btn-outline-primary"
            href={src}
            download={photoFileName(filePrefix, label)}
            target="_blank"
            rel="noopener noreferrer"
          >
            Download
          </a>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={() =>
              printPhoto({
                title: filePrefix,
                fieldLabel: label,
                imageUrl: src,
              })
            }
          >
            Print
          </button>
          <button
            type="button"
            className="btn btn-sm btn-link"
            onClick={() => setShowFullSize(true)}
          >
            Open full size
          </button>
        </div>
      </div>

      <ImageModal
        show={showFullSize}
        image={url}
        alt={label}
        onClose={() => setShowFullSize(false)}
      />
    </>
  );
}

export function printBespokePhotos(request) {
  const num = String(request?.request_number || request?.id || 'request');
  const dressPhotos =
    request?.dress_photos?.length > 0
      ? request.dress_photos
      : parseBespokePhotos(request?.dress_photo);
  const referencePhotos =
    request?.reference_photos?.length > 0
      ? request.reference_photos
      : parseBespokePhotos(request?.reference_photo);

  const photos = [
    ...dressPhotos.map((url, index) => ({
      url,
      label: dressPhotos.length > 1 ? `Outfit / dress colour (${index + 1})` : 'Outfit / dress colour photo',
    })),
    ...referencePhotos.map((url, index) => ({
      url,
      label: referencePhotos.length > 1 ? `Reference / inspiration (${index + 1})` : 'Reference / inspiration photo',
    })),
  ];

  if (!photos.length) {
    window.alert('No photos on this request.');
    return;
  }

  const pages = photos
    .map((ph) => {
      const src = customerUploadMediaUrl(ph.url);
      return `<section class="photo-page">
  <h1>Custom jewelry request #${num}</h1>
  <p>${ph.label}</p>
  <img src="${src}" alt="${ph.label}" />
</section>`;
    })
    .join('');

  const html = `<!DOCTYPE html><html><head><meta charset="utf-8"/><title>Photos — ${num}</title>
<style>
  body { font-family: "Segoe UI", system-ui, sans-serif; margin: 0; color: #111; }
  .photo-page { min-height: 277mm; padding: 10mm; page-break-after: always; }
  .photo-page:last-child { page-break-after: auto; }
  h1 { font-size: 14pt; margin-bottom: 4mm; }
  p { font-size: 10pt; color: #444; margin-bottom: 8mm; }
  img { max-width: 100%; max-height: 220mm; object-fit: contain; display: block; margin: 0 auto; border: 1px solid #ccc; }
  @media print { @page { size: A4 portrait; margin: 8mm; } }
</style></head><body>${pages}</body></html>`;

  const w = window.open('', '_blank');
  if (!w) {
    window.alert('Allow pop-ups to print photos.');
    return;
  }
  w.document.write(html);
  w.document.close();
  setTimeout(() => {
    try {
      w.print();
    } catch {
      /* user can print manually */
    }
  }, 600);
}
